import {
  Box,
  Collapse,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ChurchIcon from "@mui/icons-material/Church";
import { FC, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import useAuth from "../../hooks/useAuth";
import { brandGradient } from "../../theme/themeColors";
import { IMenuItem, MenuList } from "./MenuList";

export const SIDEBAR_WIDTH = 240;

const MainMenu = styled.div`
  width: ${SIDEBAR_WIDTH}px;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1200;
  background: ${brandGradient};
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

interface SideNavBarProps {
  showMobileSideBar: boolean;
  closeMobileSideBar: () => void;
  toggleMobileSideBar?: () => void;
}

const readableRole = (userType: string) =>
  userType ? userType.replace(/_/g, " ") : "Member";

const SideBarContent: FC<{ onNavigate?: () => void }> = ({ onNavigate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});

  const menuItems = MenuList(
    user?.user_type || "",
    user?.is_system_admin || false,
    user?.is_edify_staff || false,
    user?.is_parish_coordinator || false,
    user?.is_school_proprietor || false
  );

  // Active state is derived from the URL, so it survives reloads and
  // back/forward navigation rather than tracking the last click.
  const isActive = (path?: string) =>
    path
      ? location.pathname === path ||
        location.pathname.startsWith(`${path}/`)
      : false;

  const hasActiveChild = (item: IMenuItem): boolean =>
    item.subMenu?.some((child) => isActive(child.path) || hasActiveChild(child)) ??
    false;

  const toggleMenu = (title: string) =>
    setOpenMenus((prev) => ({ ...prev, [title]: !prev[title] }));

  const handleNavClick = (path: string) => {
    navigate(path);
    onNavigate?.();
  };

  const renderItem = (item: IMenuItem, depth = 0) => {
    const children = item.subMenu?.filter((child) => child.visible) ?? [];
    const hasChildren = children.length > 0;
    const active = !hasChildren && isActive(item.path);
    const branchActive = hasActiveChild(item);
    const isOpen = openMenus[item.title] ?? branchActive;
    const Icon = item.Icon;

    return (
      <Box key={`${item.title}-${item.path ?? depth}`}>
        <ListItem disablePadding sx={{ mb: "2px" }}>
          <ListItemButton
            onClick={() =>
              hasChildren ? toggleMenu(item.title) : handleNavClick(item.path!)
            }
            sx={{
              mx: 1,
              borderRadius: "8px",
              py: 1,
              pl: depth > 0 ? 4 : 1.5,
              pr: 1,
              backgroundColor: active
                ? "rgba(255,255,255,0.18)"
                : branchActive
                ? "rgba(255,255,255,0.08)"
                : "transparent",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
              transition: "background-color 0.2s",
            }}
          >
            {Icon && (
              <ListItemIcon
                sx={{
                  minWidth: 34,
                  color:
                    active || branchActive ? "#fff" : "rgba(255,255,255,0.65)",
                }}
              >
                <Icon sx={{ fontSize: 18 }} />
              </ListItemIcon>
            )}
            <ListItemText
              primary={item.title}
              primaryTypographyProps={{
                fontSize: 13,
                fontWeight: active ? 700 : 500,
                color:
                  active || branchActive ? "#fff" : "rgba(255,255,255,0.75)",
                fontFamily: "'Montserrat', sans-serif",
              }}
            />
            {hasChildren &&
              (isOpen ? (
                <ExpandLess
                  sx={{ fontSize: 16, color: "rgba(255,255,255,0.65)" }}
                />
              ) : (
                <ExpandMore
                  sx={{ fontSize: 16, color: "rgba(255,255,255,0.65)" }}
                />
              ))}
            {active && (
              <Box
                sx={{
                  width: 3,
                  height: 20,
                  borderRadius: 2,
                  background: "#fff",
                  ml: 1,
                }}
              />
            )}
          </ListItemButton>
        </ListItem>

        {hasChildren && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List disablePadding>
              {children.map((child) => renderItem(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Brand */}
      <Box
        sx={{
          px: 2.5,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: "1px solid rgba(255,255,255,0.12)",
          mb: 1,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            flexShrink: 0,
            borderRadius: "10px",
            background: "rgba(255,255,255,0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ChurchIcon sx={{ fontSize: 20, color: "#fff" }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            sx={{
              color: "#fff",
              fontWeight: 800,
              fontSize: 13,
              lineHeight: 1.2,
              fontFamily: "'Montserrat', sans-serif",
            }}
          >
            Parish Fellowship
          </Typography>
          <Typography
            noWrap
            sx={{
              color: "rgba(255,255,255,0.6)",
              fontWeight: 500,
              fontSize: 10,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            Edify Platform
          </Typography>
        </Box>
      </Box>

      {/* Signed-in user */}
      {user && (
        <Box sx={{ px: 2.5, mb: 1.5 }}>
          <Box
            sx={{
              background: "rgba(255,255,255,0.1)",
              borderRadius: "8px",
              px: 1.5,
              py: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                flexShrink: 0,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              {(user.first_name?.[0] || user.email?.[0] || "U").toUpperCase()}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 12,
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                {user.first_name} {user.last_name}
              </Typography>
              <Typography
                noWrap
                sx={{
                  color: "rgba(255,255,255,0.6)",
                  fontSize: 10,
                  fontWeight: 500,
                  textTransform: "capitalize",
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                {readableRole(user.user_type)}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Navigation */}
      <List disablePadding sx={{ flex: 1, pb: 2 }}>
        {menuItems
          .filter((item) => item.visible)
          .map((item) => renderItem(item))}
      </List>

      {/* Footer */}
      <Box sx={{ px: 2.5, py: 2, borderTop: "1px solid rgba(255,255,255,0.12)" }}>
        <Typography
          sx={{
            color: "rgba(255,255,255,0.4)",
            fontSize: 10,
            fontWeight: 500,
            fontFamily: "'Montserrat', sans-serif",
          }}
        >
          © {new Date().getFullYear()} Parish Fellowship
        </Typography>
      </Box>
    </Box>
  );
};

const DashboardSideBar: FC<SideNavBarProps> = ({
  showMobileSideBar,
  closeMobileSideBar,
}) => {
  const downMd = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  if (downMd) {
    return (
      <Drawer
        anchor="left"
        open={showMobileSideBar}
        onClose={closeMobileSideBar}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: { width: SIDEBAR_WIDTH, background: brandGradient, border: 0 },
        }}
      >
        <SideBarContent onNavigate={closeMobileSideBar} />
      </Drawer>
    );
  }

  return (
    <MainMenu>
      <SideBarContent />
    </MainMenu>
  );
};

export default DashboardSideBar;
